import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { useTranslation } from "react-i18next";

const LEGEND_MAX_LENGTH = 120;
const NOTES_MAX_LENGTH = 2000;
const NO_RESPONSIBLE_VALUE = "none";

export interface ResponsibleSource {
  readonly id: number;
  readonly name?: string | null;
  readonly email?: string | null;
}

export interface ResponsibleOption {
  readonly id: number;
  readonly label: string;
  readonly email: string | null;
}

export function mergeResponsibleOptions(
  sources: readonly ResponsibleSource[]
): readonly ResponsibleOption[] {
  const options = new Map<number, ResponsibleOption>();

  for (const source of sources) {
    if (options.has(source.id)) continue;

    const name = source.name?.trim();
    const email = source.email?.trim() || null;
    options.set(source.id, {
      id: source.id,
      label: name || email || String(source.id),
      email,
    });
  }

  return [...options.values()];
}

interface MeetingMetadataFieldsProps {
  readonly legenda: string;
  readonly observacoes: string;
  readonly responsavelId: number | null;
  readonly responsibleOptions: readonly ResponsibleOption[];
  readonly canEdit: boolean;
  readonly onLegendaChange: (value: string) => void;
  readonly onObservacoesChange: (value: string) => void;
  readonly onResponsavelChange: (value: number | null) => void;
}

export function MeetingMetadataFields({
  legenda,
  observacoes,
  responsavelId,
  responsibleOptions,
  canEdit,
  onLegendaChange,
  onObservacoesChange,
  onResponsavelChange,
}: MeetingMetadataFieldsProps) {
  const { t } = useTranslation("common");

  return (
    <div className="grid min-w-0 grid-cols-1 gap-3 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-3">
      <div className="min-w-0 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="meeting-header-legend">{t("editor.header_legend")}</Label>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {legenda.length}/{LEGEND_MAX_LENGTH}
          </span>
        </div>
        <Input
          id="meeting-header-legend"
          value={legenda}
          maxLength={LEGEND_MAX_LENGTH}
          placeholder={t("editor.header_legend_placeholder")}
          disabled={!canEdit}
          onChange={(event) => onLegendaChange(event.target.value)}
        />
      </div>

      <div className="min-w-0 space-y-1.5">
        <Label id="meeting-header-responsible-label">
          {t("editor.header_responsible")}
        </Label>
        <Select
          value={responsavelId === null ? NO_RESPONSIBLE_VALUE : String(responsavelId)}
          disabled={!canEdit}
          onValueChange={(value) =>
            onResponsavelChange(value === NO_RESPONSIBLE_VALUE ? null : Number(value))
          }
        >
          <SelectTrigger
            aria-labelledby="meeting-header-responsible-label"
            aria-label={t("editor.header_responsible")}
            className="min-w-0"
          >
            <SelectValue placeholder={t("editor.header_responsible_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_RESPONSIBLE_VALUE}>
              {t("editor.header_responsible_none")}
            </SelectItem>
            {responsibleOptions.map((option) => (
              <SelectItem key={option.id} value={String(option.id)}>
                <span className="block min-w-0 truncate">
                  {option.label}
                  {option.email && option.email !== option.label ? ` (${option.email})` : null}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="min-w-0 space-y-1.5 sm:col-span-2 lg:col-span-1">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="meeting-header-notes">{t("editor.header_notes")}</Label>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {observacoes.length}/{NOTES_MAX_LENGTH}
          </span>
        </div>
        <Textarea
          id="meeting-header-notes"
          value={observacoes}
          maxLength={NOTES_MAX_LENGTH}
          placeholder={t("editor.header_notes_placeholder")}
          disabled={!canEdit}
          rows={2}
          className="min-h-10 resize-y"
          onChange={(event) => onObservacoesChange(event.target.value)}
        />
      </div>
    </div>
  );
}
